import { computed, makeObservable, observable } from 'mobx';
import { notify } from 'messages';

import { getNodeInstanceWaitEvents, type WaitEvent } from 'api/node_instances/operations/getNodeInstanceWaitEvents';
import { isRaiseable, raiseWaitEvent } from 'api/events/endpoints/raiseWaitEvent';
import { toError } from 'helpers/toError';

/**
 * MobX-backed behaviour for the Wait Events tab. Owns the wait-event list, its
 * load/refresh lifecycle, and the per-row / bulk raise actions (firing toasts
 * directly, like EditModel). The component is a pure `observer` that builds one
 * of these per node instance and renders its state — all the testable logic
 * (dedup-by-eventId, raise + auto-refresh, busy tracking, bulk tally) lives
 * here and needs no React to exercise.
 *
 * Raise-all confirmation is intentionally NOT here: confirming is a dialog the
 * view renders. Individual raises don't confirm at all, matching the events
 * overview page's Raise action.
 */
export class WaitEventsModel {
  private readonly _nodeInstanceId: number | string | null;

  private _events: readonly WaitEvent[] = [];
  private _loading = false;
  private _refreshing = false;
  private _raisingAll = false;
  private _error: string | null = null;
  // eventIds with a raise in flight — disables that row's button (and the
  // duplicate rows it stands in for, since they share the eventId).
  private _busyIds: ReadonlySet<number> = new Set();

  constructor(nodeInstanceId: number | string | null) {
    this._nodeInstanceId = nodeInstanceId;
    makeObservable<WaitEventsModel, '_events' | '_loading' | '_refreshing' | '_raisingAll' | '_error' | '_busyIds'>(this, {
      _events: observable.ref,
      _loading: observable,
      _refreshing: observable,
      _raisingAll: observable,
      _error: observable,
      _busyIds: observable.ref,
      primary: computed,
      pending: computed,
    });
  }

  get error(): string | null {
    return this._error;
  }

  get events(): readonly WaitEvent[] {
    return this._events;
  }

  /** True while a refresh or a bulk raise is in flight — gates the toolbar. */
  get isBusy(): boolean {
    return this._refreshing || this._raisingAll;
  }

  get isLoading(): boolean {
    return this._loading;
  }

  get isRaisingAll(): boolean {
    return this._raisingAll;
  }

  get isRefreshing(): boolean {
    return this._refreshing;
  }

  /** Not-yet-raised events that own their Raise action (deduped by eventId). */
  get pending(): readonly WaitEvent[] {
    return this._events.filter((e, i) => this.primary.has(i) && !e.raised);
  }

  /**
   * Row indices that own the Raise action. The same event definition can appear twice — once per source (node snapshot vs
   * flow-child path). Raising is non-idempotent and `raised` is a global per-event_id predicate, so only the FIRST raiseable
   * occurrence of an eventId owns the Raise action; later duplicates share its raised state and would otherwise
   * double-insert, so they get no button.
   */
  get primary(): ReadonlySet<number> {
    const seen = new Set<number>();
    const primary = new Set<number>();
    this._events.forEach((event, index) => {
      if (!isRaiseable(event) || event.eventId === null) return;
      if (seen.has(event.eventId)) return;
      seen.add(event.eventId);
      primary.add(index);
    });
    return primary;
  }

  /** Whether the row at `index` owns the Raise action for its event. */
  canRaise(index: number): boolean {
    return this.primary.has(index);
  }

  /** Whether the event on this row (by eventId) has a raise in flight. */
  isRowBusy(event: WaitEvent): boolean {
    return event.eventId !== null && this._busyIds.has(event.eventId);
  }

  /** Initial load — drives the spinner. Kicked off by the view on mount. */
  load = async (): Promise<void> => {
    if (this._nodeInstanceId === null || this._nodeInstanceId === undefined) {
      return;
    }
    this._loading = true;
    await this.fetch();
    this._loading = false;
  };

  /** Manual refresh — same fetch, drives the toolbar's spinner instead. */
  refresh = async (): Promise<void> => {
    this._refreshing = true;
    await this.fetch();
    this._refreshing = false;
  };

  raise = async (event: WaitEvent): Promise<void> => {
    if (event.eventId === null) return;
    const eventId = event.eventId;
    this.setBusy(eventId, true);
    try {
      await raiseWaitEvent(event);
      notify.success(`Raised "${event.eventName}".`);
      await this.fetch();
    } catch (err) {
      notify.error(toError(err).message);
    } finally {
      this.setBusy(eventId, false);
    }
  };

  raiseAll = async (): Promise<void> => {
    const pending = this.pending;
    if (pending.length === 0) return;

    this._raisingAll = true;
    let raised = 0;
    const failures: string[] = [];
    // Sequential: small N, and it avoids firing a burst of raises at once.
    for (const event of pending) {
      try {
        await raiseWaitEvent(event);
        raised += 1;
      } catch (err) {
        failures.push(`${event.eventName}: ${toError(err).message}`);
      }
    }
    if (raised > 0) {
      notify.success(`Raised ${raised} event${raised === 1 ? '' : 's'}.`);
    }
    if (failures.length > 0) {
      notify.error(`Failed to raise ${failures.length}: ${failures.join('; ')}`);
    }
    this._raisingAll = false;
    await this.fetch();
  };

  private fetch = async (): Promise<void> => {
    if (this._nodeInstanceId === null || this._nodeInstanceId === undefined) {
      return;
    }
    this._error = null;
    try {
      this._events = await getNodeInstanceWaitEvents(this._nodeInstanceId);
    } catch (err) {
      console.error(err);
      this._error = 'Failed to load wait events.';
    }
  };

  private setBusy(eventId: number, busy: boolean): void {
    const next = new Set(this._busyIds);
    if (busy) {
      next.add(eventId);
    } else {
      next.delete(eventId);
    }
    this._busyIds = next;
  }
}
